import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { Download, Eye, FileImage, FileText, File as FileIcon } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { apiClient } from '@/lib/api/client'
import type { ParentTicketAttachment } from '@/types/ticket'
import {
  attachmentFileExtension,
  attachmentFileKind,
  attachmentThumbnailColors,
  attachmentThumbnailLabel,
  formatFileSize,
  type AttachmentFileKind,
} from '@/lib/utils/parentTicketAttachments'

type LoadedFile = {
  blobUrl: string
  blob: Blob
}

async function fetchAttachmentFile(ticketId: string, attachmentId: number): Promise<LoadedFile> {
  const apiBaseUrl = apiClient.defaults.baseURL ?? ''
  const apiUrl = `${apiBaseUrl}/tickets/${ticketId}/attachments/${attachmentId}/file`
  const res = await apiClient.get(apiUrl, { responseType: 'blob' })
  const blob = res.data as Blob
  return { blob, blobUrl: URL.createObjectURL(blob) }
}

function FileKindIcon({ kind, size = 20 }: { kind: AttachmentFileKind; size?: number }) {
  if (kind === 'image') return <FileImage size={size} aria-hidden />
  if (kind === 'pdf' || kind === 'document') return <FileText size={size} aria-hidden />
  return <FileIcon size={size} aria-hidden />
}

function AttachmentThumbnail({
  kind,
  fileName,
  imageUrl,
  loading,
}: {
  kind: AttachmentFileKind
  fileName: string
  imageUrl?: string | null
  loading?: boolean
}) {
  const colors = useThemeColors()
  const thumbColors = attachmentThumbnailColors(kind)
  const label = attachmentThumbnailLabel(kind, fileName)

  if (kind === 'image' && imageUrl) {
    return (
      <Image
        src={imageUrl}
        alt=""
        w="44px"
        h="44px"
        objectFit="cover"
        borderRadius="md"
        border="1px solid"
        borderColor={thumbColors.border}
      />
    )
  }

  return (
    <Box
      w="44px"
      h="44px"
      borderRadius="md"
      bg={thumbColors.bg}
      color={thumbColors.color}
      display="flex"
      alignItems="center"
      justifyContent="center"
      border="1px solid"
      borderColor={thumbColors.border}
      flexShrink={0}
      overflow="hidden"
    >
      {loading ? (
        <Spinner size="sm" color={thumbColors.color} />
      ) : kind === 'image' ? (
        <FileImage size={20} aria-hidden />
      ) : label.length <= 4 ? (
        <Text fontSize="0.6rem" fontWeight="800" letterSpacing="0.02em" lineHeight="1.1" textAlign="center" px={0.5}>
          {label}
        </Text>
      ) : (
        <VStack spacing={0}>
          <FileKindIcon kind={kind} size={16} />
          <Text fontSize="0.5rem" fontWeight="700" lineHeight="1">
            {label.slice(0, 4)}
          </Text>
        </VStack>
      )}
    </Box>
  )
}

function AttachmentPreviewModal({
  isOpen,
  onClose,
  attachment,
  blobUrl,
  kind,
}: {
  isOpen: boolean
  onClose: () => void
  attachment: ParentTicketAttachment
  blobUrl: string
  kind: AttachmentFileKind
}) {
  const colors = useThemeColors()

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={kind === 'image' ? '4xl' : '6xl'} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader fontSize="md" pr={12}>
          {attachment.fileName}
          <Text as="span" fontSize="xs" color={colors.textSecondary} fontWeight="500" ml={2}>
            {formatFileSize(attachment.fileSize)}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {kind === 'image' ? (
            <Image
              src={blobUrl}
              alt={attachment.fileName}
              maxW="100%"
              maxH="70vh"
              objectFit="contain"
              mx="auto"
              borderRadius="md"
            />
          ) : kind === 'pdf' ? (
            <Box
              as="iframe"
              src={blobUrl}
              title={attachment.fileName}
              w="100%"
              h={{ base: '60vh', md: '70vh' }}
              border="1px solid"
              borderColor={colors.borderLight}
              borderRadius="md"
            />
          ) : (
            <VStack spacing={4} py={8}>
              <FileKindIcon kind={kind} size={40} />
              <Text fontSize="sm" color={colors.textSecondary} textAlign="center">
                Preview is not available for this file type. Use Download to open it on your device.
              </Text>
              <Button
                as="a"
                href={blobUrl}
                download={attachment.fileName}
                colorScheme="blue"
                size="sm"
                leftIcon={<Download size={16} />}
              >
                Download {attachment.fileName}
              </Button>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function AttachmentRow({
  ticketId,
  attachment,
}: {
  ticketId: string
  attachment: ParentTicketAttachment
}) {
  const colors = useThemeColors()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [loading, setLoading] = useState(false)
  const [thumbLoading, setThumbLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState<LoadedFile | null>(null)
  const loadedRef = useRef<LoadedFile | null>(null)

  const kind = attachmentFileKind(attachment.fileName, attachment.mimeType)
  const ext = attachmentFileExtension(attachment.fileName)
  const sizeLabel = formatFileSize(attachment.fileSize)
  const canPreview = kind === 'image' || kind === 'pdf'

  const revokeLoaded = useCallback(() => {
    if (loadedRef.current?.blobUrl) {
      URL.revokeObjectURL(loadedRef.current.blobUrl)
    }
    loadedRef.current = null
    setLoaded(null)
  }, [])

  useEffect(() => () => revokeLoaded(), [revokeLoaded])

  const ensureLoaded = useCallback(async (): Promise<LoadedFile | null> => {
    if (!attachment.id) return null
    if (loadedRef.current) return loadedRef.current

    setLoading(true)
    setError(null)
    try {
      const next = await fetchAttachmentFile(ticketId, attachment.id)
      loadedRef.current = next
      setLoaded(next)
      return next
    } catch {
      setError('Could not load file')
      return null
    } finally {
      setLoading(false)
    }
  }, [attachment.id, ticketId])

  useEffect(() => {
    if (kind !== 'image' || !attachment.id) return

    let cancelled = false
    setThumbLoading(true)

    void fetchAttachmentFile(ticketId, attachment.id)
      .then((next) => {
        if (cancelled) {
          URL.revokeObjectURL(next.blobUrl)
          return
        }
        loadedRef.current = next
        setLoaded(next)
      })
      .catch(() => {
        if (!cancelled) setError('Could not load thumbnail')
      })
      .finally(() => {
        if (!cancelled) setThumbLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [kind, attachment.id, ticketId])

  const handleView = async () => {
    const file = await ensureLoaded()
    if (file) onOpen()
  }

  const handleDownload = async () => {
    const file = await ensureLoaded()
    if (!file) return

    const link = document.createElement('a')
    link.href = file.blobUrl
    link.download = attachment.fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  const handleClosePreview = () => {
    onClose()
  }

  return (
    <>
      <Box
        border="1px solid"
        borderColor={colors.borderLight}
        borderRadius="lg"
        bg={colors.cardBg ?? 'white'}
        p={3}
        transition="border-color 0.15s ease, box-shadow 0.15s ease"
        _hover={{ borderColor: colors.border, boxShadow: 'sm' }}
      >
        <HStack align="flex-start" spacing={3}>
          <AttachmentThumbnail
            kind={kind}
            fileName={attachment.fileName}
            imageUrl={kind === 'image' ? loaded?.blobUrl : null}
            loading={kind === 'image' ? thumbLoading : false}
          />

          <Box flex="1" minW={0}>
            <Tooltip label={attachment.fileName} hasArrow openDelay={400}>
              <Text fontSize="sm" fontWeight="600" color={colors.text} noOfLines={1}>
                {attachment.fileName}
              </Text>
            </Tooltip>
            <HStack spacing={2} mt={1} flexWrap="wrap">
              <Text fontSize="xs" color={colors.textSecondary}>
                {ext} · {sizeLabel}
              </Text>
            </HStack>
            {error ? (
              <Text fontSize="xs" color="red.500" mt={1}>
                {error}
              </Text>
            ) : null}
          </Box>

          <HStack spacing={1} flexShrink={0}>
            {canPreview ? (
              <Tooltip label="View" hasArrow>
                <IconButton
                  aria-label={`View ${attachment.fileName}`}
                  icon={loading ? <Spinner size="sm" /> : <Eye size={16} />}
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                  isDisabled={loading || !attachment.id}
                  onClick={() => void handleView()}
                />
              </Tooltip>
            ) : null}
            <Tooltip label="Download" hasArrow>
              <IconButton
                aria-label={`Download ${attachment.fileName}`}
                icon={loading && !canPreview ? <Spinner size="sm" /> : <Download size={16} />}
                size="sm"
                variant="outline"
                colorScheme="blue"
                isDisabled={loading || !attachment.id}
                onClick={() => void handleDownload()}
              />
            </Tooltip>
          </HStack>
        </HStack>
      </Box>

      {loaded && isOpen ? (
        <AttachmentPreviewModal
          isOpen={isOpen}
          onClose={handleClosePreview}
          attachment={attachment}
          blobUrl={loaded.blobUrl}
          kind={kind}
        />
      ) : null}
    </>
  )
}

function AttachmentFallbackRow({ attachment }: { attachment: ParentTicketAttachment }) {
  const colors = useThemeColors()
  const kind = attachmentFileKind(attachment.fileName, attachment.mimeType)

  return (
    <Box
      border="1px dashed"
      borderColor={colors.borderLight}
      borderRadius="lg"
      p={3}
    >
      <HStack spacing={3} align="center">
        <AttachmentThumbnail kind={kind} fileName={attachment.fileName} />
        <Box minW={0}>
          <Text fontSize="sm" fontWeight="600" noOfLines={1}>
            {attachment.fileName}
          </Text>
          <Text fontSize="xs" color={colors.textSecondary} mt={1}>
            {formatFileSize(attachment.fileSize)}
          </Text>
        </Box>
      </HStack>
    </Box>
  )
}

type Props = {
  ticketId?: string | null
  attachments?: ParentTicketAttachment[]
  title?: string
  emptyMessage?: string
  sourceFilter?: 'form' | 'task' | 'all'
  hideTitle?: boolean
}

export function ParentTicketAttachmentsList({
  ticketId,
  attachments = [],
  title = 'Attachments',
  emptyMessage = 'No attachments.',
  sourceFilter = 'all',
  hideTitle = false,
}: Props) {
  const colors = useThemeColors()
  const filtered = attachments.filter((item) =>
    sourceFilter === 'all' ? true : item.source === sourceFilter,
  )

  return (
    <Box>
      {!hideTitle ? (
        <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={2}>
          {title}
        </Text>
      ) : null}
      {filtered.length === 0 ? (
        <Text fontSize="sm" color={colors.textSecondary}>
          {emptyMessage}
        </Text>
      ) : (
        <VStack align="stretch" spacing={2}>
          {filtered.map((attachment) =>
            ticketId && attachment.id ? (
              <AttachmentRow
                key={attachment.id}
                ticketId={ticketId}
                attachment={attachment}
              />
            ) : (
              <AttachmentFallbackRow
                key={attachment.filePath ?? attachment.fileName}
                attachment={attachment}
              />
            ),
          )}
        </VStack>
      )}
    </Box>
  )
}
